/**
 * Copyright © 2026 Adnuntius AS.
 */
import './quill-setup';

import {Component, EventEmitter, forwardRef, Inject, Input, NgZone, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {FormsModule} from '@angular/forms';
import {QuillModule} from 'ngx-quill';

import template from './admin-rich-text-field.component.html';
import {TOOLBAR_BUTTONS} from './notes-constants';
import {buildQuillModules} from './quill-setup';
import {AppSharedModule} from '../../app-shared.module';

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule, QuillModule],
  selector: 'adn-admin-rich-text-field',
  template,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AdminRichTextFieldComponent),
    multi: true
  }]
})
export class AdminRichTextFieldComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() toolbar: string[][] = TOOLBAR_BUTTONS;
  @Input() placeholder = '';
  @Output() fieldChange = new EventEmitter<void>();

  htmlMode = false;
  htmlValue = '';
  disabled = false;
  quillModules: Record<string, any>;

  onChange: (value: string) => void = () => {
  };
  onTouched: () => void = () => {
  };

  constructor(@Inject(NgZone) private ngZone: NgZone) {
  }

  ngOnInit(): void {
    this.buildQuillModules();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.toolbar && !changes.toolbar.firstChange) {
      this.buildQuillModules();
    }
  }

  writeValue(value: string | string[] | null | undefined): void {
    this.htmlValue = this.normalizeValue(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onEditorChange(value: string): void {
    const normalizedValue = value || '';
    this.htmlValue = normalizedValue;
    this.onChange(this.htmlValue);
    this.fieldChange.emit();
  }

  onSourceInput(value: string): void {
    if (value === this.htmlValue) {
      return;
    }
    this.htmlValue = value;
    this.onChange(value);
    this.fieldChange.emit();
  }

  private toggleHtmlMode(): void {
    this.ngZone.run(() => {
      this.htmlMode = !this.htmlMode;
    });
  }

  private buildQuillModules(): void {
    this.quillModules = buildQuillModules(this.toolbar || TOOLBAR_BUTTONS, {
      html: () => this.toggleHtmlMode()
    });
  }

  private normalizeValue(value: string | string[] | null | undefined): string {
    if (Array.isArray(value)) {
      return value.join('');
    }
    return value || '';
  }
}
